import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DocstoreSections,
  getSelectedSectionFromParams,
} from "../../../../../model";
import { FilterType } from "../../docstore-section-filters/hooks/use-filters";
import {
  getFieldValue,
  getSearchableValues,
} from "../../docstore-section-filters/model/constants";
import { createDocstoreTableConfig } from "../model/config";
import { dataSource as mockDataSource } from "../model/constants";
import { ColumnConfig, DataType, TableConfig } from "../model/types";

export function useConfigurableTable(
  actionComponent?: (record: DataType) => ReactNode,
) {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [filteredData, setFilteredData] = useState<DataType[]>([]);
  const [filters, setFilters] = useState<FilterType[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const selectedSection = getSelectedSectionFromParams(searchParams);

  const tableConfig = (
    createDocstoreTableConfig()[
      selectedSection as Exclude<keyof typeof DocstoreSections, "TRASH">
    ] as TableConfig
  ).columns.map((column: ColumnConfig) => {
    if (column.key === "action") {
      return { ...column, render: (_, record) => actionComponent?.(record) };
    }
    return column;
  });

  const applyFilters = useCallback(
    (data: DataType[], filters: FilterType[], search: string) => {
      let filtered = [...data];

      if (search.trim()) {
        const searchLower = search.toLowerCase();
        filtered = filtered.filter((item) => {
          const searchableValues = getSearchableValues(item);
          return searchableValues.some((value) =>
            value.toLowerCase().includes(searchLower),
          );
        });
      }

      filters.forEach((filter) => {
        if (filter.value.trim()) {
          filtered = filtered.filter((item) => {
            const itemValue = getFieldValue(item, filter.field);
            const filterValue = filter.value.toLowerCase();

            switch (filter.operator) {
              case "contains":
                return itemValue.toLowerCase().includes(filterValue);
              case "equals":
                return itemValue.toLowerCase() === filterValue;
              case "starts_with":
                return itemValue.toLowerCase().startsWith(filterValue);
              case "ends_with":
                return itemValue.toLowerCase().endsWith(filterValue);
              default:
                return true;
            }
          });
        }
      });

      return filtered;
    },
    [],
  );

  const handleFiltersChange = useCallback(
    (newFilters: FilterType[], newSearchValue: string) => {
      setFilters(newFilters);
      setSearchValue(newSearchValue);
    },
    [setFilters, setSearchValue],
  );

  useEffect(() => {
    const filtered = applyFilters(dataSource, filters, searchValue);
    setFilteredData(filtered);
  }, [dataSource, filters, searchValue, applyFilters]);

  const loadData = async () => {
    setLoading(true);
    try {
      setDataSource(mockDataSource);
    } catch {
      // mock data load failed
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return {
    selectedSection,
    tableConfig,
    loading,
    dataSource: filteredData,
    loadData,
    handleFiltersChange,
    activeFiltersCount:
      filters.filter((filter) => filter.value.trim() !== "").length +
      (searchValue.trim() ? 1 : 0),
  };
}
